import { Input, PolicyDispatchingPermission, assets } from '@pulseops/common'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { ActivityIndicator, StyleProp, View, ViewStyle, Text } from 'react-native'
import { ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/function'

import { borderedInputStyle } from './styles/policyDispatchingStyles'
import PolicyDispatchingDialog from './components/PolicyDispatchingDialog'
import { PolicyDispatchingService, QuickSearchAuthorizationLetter } from './services/PolicyDispatchingService'
import { getPermissions } from './utils/policyDispatchingUtils'

type ControlledInputProps = {
  control: any
  name: string
  title: string
  required?: boolean
  containerStyle?: StyleProp<ViewStyle>
  disabled?: boolean
}

type FormValues = {
  authorizationLetterId: string
  ownerCode: string
  ownerName: string
  authorizedPerson: string
  approver: string
  effectiveFromDate: string
  effectiveToDate: string
  createDate: string
  creator: string
}

const ControlledInput = ({ control, name, title, required, containerStyle, disabled = true }: ControlledInputProps) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
        <Input
          title={title}
          onChange={onChange}
          onBlur={onBlur}
          value={value}
          errorMessage={error?.message}
          containerStyle={containerStyle as any}
          required={required}
          inputStyle={borderedInputStyle}
          filled
          alwayShowUnderline
          disabled={disabled}
        />
      )}
    />
  )
}

const AuthorizeLetterSearchSection = ({
  control,
  onSearch,
  isLoading,
  allowSearch
}: {
  control: any
  onSearch: () => void
  isLoading: boolean
  allowSearch: boolean
}) => {
  return (
    <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 24, marginBottom: 24 } as any}>
      <View style={{ width: '20%' } as any}>
        <ControlledInput
          control={control}
          name="authorizationLetterId"
          title="Mã số giấy ủy quyền"
          containerStyle={{ flex: 1 }}
          disabled={false}
          required
        />
      </View>
      <button
        style={{
          marginTop: 24,
          minHeight: 40,
          ...(isLoading || !allowSearch ? styles.disabledButton : styles.submitButton)
        }}
        type="button"
        onClick={onSearch}
        disabled={isLoading || !allowSearch}
      >
        <span style={styles.mr_5}>
          <assets.SearchWhite />
        </span>
        Tìm
      </button>
    </View>
  )
}

const AuthorizeLetterDetailsSection = ({
  control,
  watch,
  authorizedContractOf
}: {
  control: any
  watch: any
  authorizedContractOf: string
}) => {
  return (
    <View style={{ display: 'flex', flexDirection: 'column', gap: 16 } as any}>
      <View style={styles.row}>
        <View style={{ width: '20%' } as any}>
          <ControlledInput control={control} name="ownerCode" title="Người ủy quyền" />
        </View>
        {watch('ownerName') !== '' && (
          <View style={{ flex: 1, marginTop: 22 } as any}>
            <ControlledInput control={control} name="ownerName" title="" />
          </View>
        )}
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="authorizedPerson" title="Người được ủy quyền" />
        </View>
        <View style={styles.column_1_2} />
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="approver" title="Người phê duyệt" />
        </View>
        <View style={styles.column_1_2} />
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="effectiveFromDate" title="Hiệu lực từ ngày" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="effectiveToDate" title="Hiệu lực đến ngày" />
        </View>
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="createDate" title="Ngày tạo" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="creator" title="Người tạo" />
        </View>
      </View>
      <View>
        <Text style={{ fontWeight: 'bold', marginBottom: '8px' } as any}>Được phép nhận hợp đồng của</Text>
        <Text style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 } as any}>
          Đại lý / Nhóm Đại lý: <b>{authorizedContractOf}</b>
        </Text>
      </View>
    </View>
  )
}

const toFormValues = (item: QuickSearchAuthorizationLetter): Omit<FormValues, 'authorizationLetterId'> => ({
  ownerCode: item.authorizerCode!.join(', ') || '',
  ownerName: item.authorizerName || '',
  authorizedPerson: item.authorizedPerson || '',
  approver: item.approver || '',
  effectiveFromDate: item.effectiveFromDate || '',
  effectiveToDate: item.effectiveToDate || '',
  createDate: item.createDate || '',
  creator: item.owner || ''
})

const emptyDetails: Omit<FormValues, 'authorizationLetterId'> = {
  ownerCode: '',
  ownerName: '',
  authorizedPerson: '',
  approver: '',
  effectiveFromDate: '',
  effectiveToDate: '',
  createDate: '',
  creator: ''
}

export default function PDAuthorizeLetterScreen() {
  const { control, handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      authorizationLetterId: '',
      ...emptyDetails
    }
  })
  const [authorizedContractOf, setAuthorizedContractOf] = React.useState('')
  const [isLoading, setIsLoading] = React.useState(false)
  const [modalInfo, setModalInfo] = React.useState({ title: '', message: '' })
  const [openModal, setOpenModal] = React.useState(false)
  const permissions = getPermissions()
  const allowSearch = permissions.includes(PolicyDispatchingPermission.QuickSearchSearch)

  const resetDetails = () => {
    Object.entries(emptyDetails).forEach(([field, value]) => setValue(field as keyof FormValues, value))
    setAuthorizedContractOf('')
  }

  const handleSearch = handleSubmit((values) => {
    if (!values.authorizationLetterId) {
      setModalInfo({ title: 'Tìm kiếm', message: 'Vui lòng nhập mã số giấy ủy quyền trước khi tìm kiếm.' })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.searchQuickAuthorizationLetter(values.authorizationLetterId),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          if (!('body' in response)) {
            resetDetails()
            setIsLoading(false)
            setModalInfo({ title: 'Tìm kiếm', message: 'Không tìm thấy giấy ủy quyền theo yêu cầu.' })
            setOpenModal(true)
            return
          }

          const details = toFormValues(response.body)
          Object.entries(details).forEach(([field, value]) => setValue(field as keyof FormValues, value))
          setAuthorizedContractOf((response.body.authorizedContractOf || []).join(', '))
          setIsLoading(false)
        })
      ),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          console.error('Unable to search authorization letter', error)
          resetDetails()
          setIsLoading(false)
          setModalInfo({
            title: 'Tìm kiếm thất bại',
            message: 'Không thể tải thông tin giấy ủy quyền. Vui lòng thử lại sau.'
          })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  })

  return (
    <View style={{ ...styles.container, position: 'relative' } as any}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => setOpenModal(false)}
      />
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}
      <Text style={styles.title}>Tìm nhanh</Text>
      <View style={styles.wrapper}>
        <Text style={styles.subTitle}>Giấy ủy quyền</Text>
        <AuthorizeLetterSearchSection
          control={control}
          onSearch={handleSearch}
          allowSearch={allowSearch}
          isLoading={isLoading}
        />
        <AuthorizeLetterDetailsSection control={control} authorizedContractOf={authorizedContractOf} watch={watch} />
      </View>
    </View>
  )
}

const styles: Record<string, any> = {
  container: {
    paddingTop: 0,
    paddingRight: 40,
    paddingBottom: 30,
    paddingLeft: 40,
    height: '100%',
    overflow: 'auto'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  wrapper: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  subTitle: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
    paddingTop: 4,
    paddingRight: 12,
    paddingBottom: 4,
    paddingLeft: 12,
    borderRadius: 4,
    backgroundColor: '#e7000b',
    marginBottom: 16,
    width: 'fit-content'
  },
  submitButton: {
    backgroundColor: '#ED1B2E',
    color: '#fff',
    borderWidth: 0,
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600'
  },
  disabledButton: {
    backgroundColor: '#e5e7eb',
    color: '#9ca3af',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600',
    cursor: 'not-allowed',
    opacity: 0.7
  },
  mr_5: {
    marginRight: '5px'
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
    width: '100%'
  },
  column_1_2: {
    width: 'calc(100% / 2 - 12px)'
  },
  loadingOverlay: {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 2
  }
}
